<script lang="ts" setup>
import type { IconName } from '~ds/lib/icons';
import type { SizeVariants, StateVariants } from '~kui/types/kui';

import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';

interface Props {
  ariaLabel?: string;
  icon: IconName;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'outline' | 'off-white' | 'white' | 'grey' | StateVariants;
  size?: Extract<SizeVariants, 'xs' | 'sm' | 'md'>;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  size: 'md',
  variant: 'primary',
  loading: false,
  ariaLabel: '',
});

const variantClasses = computed(() => {
  return twMerge(
    cva('flex items-center justify-center rounded-full', {
      variants: {
        disabled: {
          true: 'cursor-not-allowed opacity-50',
          false: 'cursor-auto',
        },
        size: {
          xs: 'size-5',
          sm: 'size-8',
          md: 'size-10',
        },
        variant: {
          'primary': 'bg-primary text-primary-foreground',
          'secondary': 'bg-secondary text-secondary-foreground',
          'tertiary': 'text-foreground bg-transparent',
          'outline': 'border-foreground border-2 bg-transparent',
          'off-white': 'bg-background',
          'white': 'bg-white',
          // Sits on a dark panel, where off-black would disappear into the background.
          'grey': 'bg-muted-foreground text-white',
          'success': 'bg-success-foreground text-white',
          'warning': 'bg-warning text-white',
          'danger': 'bg-destructive text-white',
          'info': 'bg-info-foreground text-white',
        },
      },
    })({
      size: props.size,
      disabled: props.disabled,
      variant: props.variant,
    }),
  );
});
</script>

<template>
  <KUICoreClickable
    :class="variantClasses"
    :disabled="disabled"
    :aria-label="ariaLabel"
  >
    <Icon
      v-if="loading"
      class="animate-spin"
      :name="ICONS.loading"
      size="lg"
    />
    <Icon
      v-else
      :name="icon"
      size="lg"
    />
  </KUICoreClickable>
</template>
